import { Link, useLocation } from "wouter";
import { Menu, LogOut, User, Wallet } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useIsAuthenticated, useLogout, getCurrentUser } from "@/hooks/use-auth";
import { useWalletBalance } from "@/hooks/use-wallet";
import { useTheme } from "@/components/ThemeProvider";

export function Navigation() {
  const [location, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const logoImg = theme === "dark" ? "/housekeeping-logo-dark.png" : "/housekeeping-logo.png";
  const isAuthenticated = useIsAuthenticated();
  const logout = useLogout();
  const user = getCurrentUser();
  const { data: walletData } = useWalletBalance();
  const walletBalance = walletData?.balance ?? 0;

  const handleLogout = () => {
    logout();
    setLocation("/");
  };

  const navItems = [
    { href: "/", label: t("nav.home") },
    { href: "/booking", label: t("nav.booking") },
    ...(isAuthenticated ? [
      { href: "/orders", label: t("nav.orders") },
      { href: "/addresses", label: t("nav.addresses") },
      { href: "#", label: t("nav.wallet") },
    ] : [
      { href: "/about", label: t("nav.about") },
    ]),
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <Link href="/" className="flex items-center gap-2.5 group">
          <img src={logoImg} alt="The Housekeeping Logo" className="h-10 w-10 object-contain flex-shrink-0" />
          <div className="flex flex-col leading-none">
            <span className="text-[11px] font-bold uppercase tracking-widest text-foreground">
              CLEANING SOLUTIONS
            </span>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium mt-0.5">
              THE HOUSEKEEPING
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <Link 
              key={item.href} 
              href={item.href}
              className={`text-sm font-medium transition-colors hover:text-primary pb-1 border-b-2 ${
                location === item.href
                  ? "text-foreground font-semibold border-secondary"
                  : "text-muted-foreground border-transparent"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <LanguageSwitcher />
          <ThemeToggle />
          {isAuthenticated ? (
            <>
              <div className="flex flex-col items-end text-end">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {user?.name || user?.phone}
                </span>
                <span className="text-xs font-medium flex items-center gap-1 text-primary">
                  <Wallet className="h-3 w-3" />
                  {t("nav.wallet")}:{" "}
                  {walletBalance.toFixed(3)} {t("services.currency")}
                </span>
              </div>
              <Button size="icon" variant="ghost" onClick={handleLogout} data-testid="button-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Link href="/booking">
              <Button size="sm" className="btn-hover" data-testid="button-get-started">
                {t("nav.getStarted")}
              </Button>
            </Link>
          )}
        </nav>

        {/* Mobile Nav */}
        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          <ThemeToggle />
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="border-0">
              <div className="flex flex-col gap-6 mt-8">
                {navItems.map((item) => (
                  <Link 
                    key={item.href} 
                    href={item.href}
                    className={`text-lg font-medium transition-colors hover:text-primary ${
                      location === item.href ? "text-primary" : "text-muted-foreground"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                {isAuthenticated ? (
                  <>
                    <div className="text-sm text-muted-foreground flex items-center gap-2 py-2">
                      <User className="h-4 w-4" />
                      {user?.name || user?.phone}
                    </div>
                    <div className="text-sm font-medium flex items-center gap-2 py-2 text-primary">
                      <Wallet className="h-4 w-4" />
                      {t("nav.wallet")}:{" "}
                      {walletBalance.toFixed(3)} {t("services.currency")}
                    </div>
                    <Button variant="outline" className="w-full" onClick={() => { handleLogout(); setIsOpen(false); }}>
                      <LogOut className="h-4 w-4 me-2" />
                      {t("login.logout")}
                    </Button>
                  </>
                ) : (
                  <Link href="/booking" onClick={() => setIsOpen(false)}>
                    <Button className="w-full">
                      {t("nav.booking")}
                    </Button>
                  </Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
